import { lazy, Suspense } from 'react';
import { Outlet, Routes, Route, useLocation } from 'react-router-dom';
import { RequireAuth } from './require-auth';
import { PageLoader } from '@/components/common/page-loader';
import { CookieBanner } from '@/components/legal/cookie-banner';
import { useAuth } from '@/hooks/use-auth';
import { RouteScrollManager } from '@/hooks/use-catalog-scroll';
import { PublicationModeProvider } from './publication-context';
import { CoachRuntimeBoundary } from '@/components/coach/coach-runtime-boundary';

const AppLayout = lazy(() => import('@/components/layout/app-layout').then((module) => ({
  default: module.AppLayout,
})));

const Login = lazy(() => import('./routes/login').then(m => ({ default: m.LoginPage })));
const Register = lazy(() => import('./routes/register').then(m => ({ default: m.RegisterPage })));
const Dashboard = lazy(() => import('./routes/today').then(m => ({ default: m.TodayPage })));
const Progress = lazy(() => import('./routes/progress').then(m => ({ default: m.ProgressPage })));
const ExercisesIndex = lazy(() => import('./routes/exercises/index').then(m => ({ default: m.ExercisesPage })));
const ExerciseDetail = lazy(() => import('./routes/exercises/$id').then(m => ({ default: m.ExerciseDetailPage })));
const ExerciseNew = lazy(() => import('./routes/exercises/new').then(m => ({ default: m.ExerciseNewPage })));
const ExerciseEdit = lazy(() => import('./routes/exercises/$id.edit').then(m => ({ default: m.ExerciseEditPage })));
const WorkoutsIndex = lazy(() => import('./routes/workouts/index').then(m => ({ default: m.WorkoutsPage })));
const WorkoutNew = lazy(() => import('./routes/workouts/new').then(m => ({ default: m.WorkoutNewPage })));
const WorkoutDetail = lazy(() => import('./routes/workouts/$id').then(m => ({ default: m.WorkoutDetailPage })));
const Plan = lazy(() => import('./routes/workouts/plan').then(m => ({ default: m.PlanWorkoutPage })));
const WorkoutProgram = lazy(() => import('./routes/workouts/program').then(m => ({ default: m.WorkoutProgramPage })));
const SupplementsIndex = lazy(() => import('./routes/supplements/index').then(m => ({ default: m.SupplementsPage })));
const SupplementDetail = lazy(() => import('./routes/supplements/$id').then(m => ({ default: m.SupplementDetailPage })));
const SupplementNew = lazy(() => import('./routes/supplements/new').then(m => ({ default: m.SupplementNewPage })));
const SupplementEdit = lazy(() => import('./routes/supplements/$id.edit').then(m => ({ default: m.SupplementEditPage })));
const NutritionIndex = lazy(() => import('./routes/nutrition/index').then(m => ({ default: m.NutritionPage })));
const NutritionProfile = lazy(() => import('./routes/nutrition/profile').then(m => ({ default: m.NutritionProfilePage })));
const NutritionConstructor = lazy(() => import('./routes/nutrition/constructor').then(m => ({ default: m.NutritionConstructorPage })));
const NutritionRecipes = lazy(() => import('./routes/nutrition/recipes').then(m => ({ default: m.NutritionRecipesPage })));
const NutritionRecipeDetail = lazy(() => import('./routes/nutrition/recipe-detail').then(m => ({ default: m.NutritionRecipeDetailPage })));
const Calculators = lazy(() => import('./routes/calculators').then(m => ({ default: m.CalculatorsPage })));
const Settings = lazy(() => import('./routes/settings').then(m => ({ default: m.SettingsPage })));
const SettingsIntegrations = lazy(() => import('./routes/settings/integrations').then(m => ({ default: m.IntegrationsPage })));
const SettingsCoach = lazy(() => import('./routes/settings/coach').then(m => ({ default: m.CoachSettingsPage })));
const Coach = lazy(() => import('./routes/coach').then(m => ({ default: m.CoachPage })));
const NotesIndex = lazy(() => import('./routes/notes/index').then(m => ({ default: m.NotesPage })));
const NoteEditor = lazy(() => import('./routes/notes/$id').then(m => ({ default: m.NoteEditorPage })));
const NotFound = lazy(() => import('./routes/not-found').then(m => ({ default: m.NotFoundPage })));
const RootRedirect = lazy(() => import('./routes/index').then(m => ({ default: m.RootRedirect })));
const Welcome = lazy(() => import('./routes/welcome').then(m => ({ default: m.WelcomePage })));
const Landing = lazy(() => import('./routes/landing-next').then(m => ({ default: m.LandingPage })));
const Demo = lazy(() => import('./routes/demo').then(m => ({ default: m.DemoPage })));
const MediaPreview = import.meta.env.DEV
  ? lazy(() => import('./routes/media-preview').then(m => ({ default: m.MediaPreviewPage })))
  : null;
const ToolBmi = lazy(() => import('./routes/tools/bmi').then(m => ({ default: m.BmiToolPage })));
const ToolBmr = lazy(() => import('./routes/tools/bmr').then(m => ({ default: m.BmrToolPage })));
const ToolTdee = lazy(() => import('./routes/tools/tdee').then(m => ({ default: m.TdeeToolPage })));
const ToolOneRm = lazy(() => import('./routes/tools/1rm').then(m => ({ default: m.OneRmToolPage })));
const Pricing = lazy(() => import('./routes/pricing').then(m => ({ default: m.PricingPage })));
const Billing = lazy(() => import('./routes/billing').then(m => ({ default: m.BillingPage })));
const LegalIndex = lazy(() => import('./routes/legal/index').then(m => ({ default: m.LegalIndexPage })));
const LegalDoc = lazy(() => import('./routes/legal/$slug').then(m => ({ default: m.LegalDocPage })));
const KnowledgeIndex = lazy(() => import('./routes/knowledge').then(m => ({ default: m.KnowledgeIndexPage })));
const KnowledgeTerm = lazy(() => import('./routes/knowledge').then(m => ({ default: m.KnowledgeTermPage })));
const BlogIndex = lazy(() => import('./routes/blog').then(m => ({ default: m.BlogIndexPage })));
const BlogArticle = lazy(() => import('./routes/blog').then(m => ({ default: m.BlogArticlePage })));

function RootGate() {
  const { isAuthed, isBooting } = useAuth();
  if (isBooting) return <PageLoader />;
  if (isAuthed) return <RootRedirect />;
  return <Landing />;
}

function PublicationGate() {
  const { isAuthed, isBooting } = useAuth();
  if (isBooting) return <PageLoader />;
  return (
    <PublicationModeProvider mode={isAuthed ? 'app' : 'public'}>
      {isAuthed ? <RequireAuth><AppLayout /></RequireAuth> : <Outlet />}
    </PublicationModeProvider>
  );
}

export function Root() {
  const { pathname } = useLocation();
  const isMediaPreview = import.meta.env.DEV && pathname === '/media-preview';

  return (
    <Suspense fallback={<PageLoader />}>
      <RouteScrollManager />
      {!isMediaPreview && <CookieBanner />}
      <CoachRuntimeBoundary>
        <Routes>
          <Route path="/" element={<RootGate />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/demo" element={<Demo />} />
          {MediaPreview && <Route path="/media-preview" element={<MediaPreview />} />}
          <Route path="/tools/bmi" element={<ToolBmi />} />
          <Route path="/tools/bmr" element={<ToolBmr />} />
          <Route path="/tools/tdee" element={<ToolTdee />} />
          <Route path="/tools/1rm" element={<ToolOneRm />} />
          <Route element={<PublicationGate />}>
            <Route path="/knowledge" element={<KnowledgeIndex />} />
            <Route path="/knowledge/:slug" element={<KnowledgeTerm />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
          </Route>
          <Route path="/legal" element={<LegalIndex />} />
          <Route path="/legal/offer" element={<LegalDoc slug="offer" />} />
          <Route path="/legal/privacy" element={<LegalDoc slug="privacy" />} />
          <Route path="/legal/consent" element={<LegalDoc slug="consent" />} />
          <Route path="/legal/terms" element={<LegalDoc slug="terms" />} />
          <Route path="/legal/cookies" element={<LegalDoc slug="cookies" />} />
          <Route path="/legal/medical-disclaimer" element={<LegalDoc slug="medical-disclaimer" />} />
          <Route path="/welcome" element={<RequireAuth><Welcome /></RequireAuth>} />
          <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/progress" element={<Progress />} />
            <Route path="/exercises" element={<ExercisesIndex />} />
            <Route path="/exercises/new" element={<ExerciseNew />} />
            <Route path="/exercises/:id" element={<ExerciseDetail />} />
            <Route path="/exercises/:id/edit" element={<ExerciseEdit />} />
            <Route path="/workouts" element={<WorkoutsIndex />} />
            <Route path="/workouts/new" element={<WorkoutNew />} />
            <Route path="/workouts/plan" element={<Plan />} />
            <Route path="/workouts/program" element={<WorkoutProgram />} />
            <Route path="/workouts/:id" element={<WorkoutDetail />} />
            <Route path="/supplements" element={<SupplementsIndex />} />
            <Route path="/supplements/new" element={<SupplementNew />} />
            <Route path="/supplements/:id" element={<SupplementDetail />} />
            <Route path="/supplements/:id/edit" element={<SupplementEdit />} />
            <Route path="/nutrition" element={<NutritionIndex />} />
            <Route path="/nutrition/profile" element={<NutritionProfile />} />
            <Route path="/nutrition/constructor" element={<NutritionConstructor />} />
            <Route path="/nutrition/recipes" element={<NutritionRecipes />} />
            <Route path="/nutrition/recipes/:id" element={<NutritionRecipeDetail />} />
            <Route path="/calculators" element={<Calculators />} />
            <Route path="/coach" element={<Coach />} />
            <Route path="/notes" element={<NotesIndex />} />
            <Route path="/notes/new" element={<NoteEditor />} />
            <Route path="/notes/:id" element={<NoteEditor />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/settings/integrations" element={<SettingsIntegrations />} />
            <Route path="/settings/coach" element={<SettingsCoach />} />
            <Route path="/billing" element={<Billing />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </CoachRuntimeBoundary>
    </Suspense>
  );
}
